import type { IPlaybackScheduleRoot } from '../models/player-registration-payload'
import type { TFrame } from '../models/playlist'

function frameCtasSig(frame: TFrame): string {
  if (frame.ctas == null || frame.ctas.length === 0) {
    return ''
  }
  return frame.ctas
    .map(p => `${p.id}\t${p.ctaId}\t${p.x0}\t${p.y0}\t${p.width}\t${p.height}`)
    .sort()
    .join('|')
}

export function framePlaybackSelectionSig(frame: TFrame): string {
  const assets = Object.values(frame.facets ?? {})
    .map(facet => `${facet?.type ?? ''}:${facet?.asset ?? ''}`)
    .sort()
    .join(',')
  return `${frame.id}\t${String(frame.duration ?? '')}\t${assets}\t${frameCtasSig(frame)}`
}

/**
 * Stable fingerprint of embedded playlist **selection pools** (frame ids, durations, facet asset URNs).
 * Ignores registration metadata churn (widget maps, HLS hints, layout noise) that should not reset
 * random/rotating embed picks on benign registration-status polls.
 */
export function embeddedPlaylistFramesSelectionFingerprint(
  embedded: Readonly<Record<string, readonly TFrame[]>> | null | undefined
): string {
  if (embedded == null) {
    return ''
  }
  const urns = Object.keys(embedded).sort((a, b) => a.localeCompare(b))
  if (urns.length === 0) {
    return ''
  }
  const parts: string[] = []
  for (const urn of urns) {
    const sig = (embedded[urn] ?? []).map(framePlaybackSelectionSig).join('|')
    parts.push(`${urn}\t${sig}`)
  }
  return parts.join('\n')
}

/**
 * Fingerprint of frame-level overlays (CTA placements) on assignment rows and embedded pools.
 * Used by the player to detect house editorial CTA add/remove without treating it as benign metadata churn.
 */
export function registrationScheduleFrameOverlaysFingerprint(
  root: IPlaybackScheduleRoot | null | undefined
): string {
  if (root == null) {
    return ''
  }
  const parts: string[] = []
  const playlists = [...(root.playlists ?? [])].sort((a, b) => a.index - b.index)
  for (const pl of playlists) {
    const sig = (pl.frames ?? []).map(framePlaybackSelectionSig).join('|')
    parts.push(`pl\t${pl.playlistUrn}\t${sig}`)
  }
  const embedded = embeddedPlaylistFramesSelectionFingerprint(root.embeddedPlaylistFrames)
  if (embedded !== '') {
    parts.push(`emb\t${embedded}`)
  }
  return parts.join('\n')
}
